/**
 * Remember which main tool the user used last, and reopen it when they come
 * back to the site's home address.
 *
 * Only the first page of a visit is redirected: React Router gives that
 * history entry the key "default", while in-app navigation (e.g. clicking
 * "Converter") creates new keys, so the Converter stays reachable. Visitors
 * without saved state (including search-engine crawlers) see the home page.
 */
import type { Location } from 'react-router-dom';

const LAST_TAB_KEY = 'p2t-last-tab';

/** The site's main sections, as nav paths. */
export const MAIN_SECTIONS = ['/', '/text-editor', '/typing-test', '/guides'] as const;

/** Map any path to its main section ("/guides/foo" → "/guides"), or null if it isn't one. */
export const sectionForPath = (pathname: string): string | null => {
  if (pathname === '/') return '/';
  return MAIN_SECTIONS.find((s) => s !== '/' && (pathname === s || pathname.startsWith(`${s}/`))) ?? null;
};

export const readLastSection = (): string | null => {
  try {
    const value = localStorage.getItem(LAST_TAB_KEY);
    return value && (MAIN_SECTIONS as readonly string[]).includes(value) ? value : null;
  } catch {
    return null;
  }
};

/**
 * Where a fresh visit to the home page should go instead, or null to stay.
 * Explicit queries/fragments on "/" are respected.
 */
export const homeRedirectTarget = (location: Pick<Location, 'key' | 'pathname' | 'search' | 'hash'>): string | null => {
  if (location.key !== 'default' || location.pathname !== '/' || location.search || location.hash) return null;
  const last = readLastSection();
  return last && last !== '/' ? last : null;
};

/** Record the section for `location` (skipping the home page we're about to redirect away from). */
export const rememberSection = (location: Pick<Location, 'key' | 'pathname' | 'search' | 'hash'>) => {
  if (homeRedirectTarget(location)) return;
  const section = sectionForPath(location.pathname);
  if (!section) return;
  try {
    localStorage.setItem(LAST_TAB_KEY, section);
  } catch {
    /* storage unavailable — the feature is a convenience */
  }
};
